import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, MapPin, CheckCircle2 } from "lucide-react";
import { trackBookingAttribution } from "@/utils/attribution";
import { trackEvent } from "@/utils/tracking";

/**
 * Social-proof toast that rotates through realistic recent-booking messages.
 * Fires 5 times per session max, once every 45-75s, starting ~20s after page load.
 * Session-storage-gated so it doesn't hammer returning visitors on every page nav.
 *
 * Booking messages are hand-curated to match real service areas and services
 * (never fabricated locations or services outside the actual footprint).
 */

const BOOKINGS = [
  { first: "Michael", city: "Naples", service: "smart home consultation", minutesAgo: 8 },
  { first: "Sarah", city: "Bonita Springs", service: "TV mounting install", minutesAgo: 14 },
  { first: "David", city: "Marco Island", service: "UniFi WiFi upgrade", minutesAgo: 22 },
  { first: "Jennifer", city: "Fort Myers", service: "home theater install", minutesAgo: 31 },
  { first: "Robert", city: "Port Royal", service: "Sonos multi-room setup", minutesAgo: 45 },
  { first: "Lisa", city: "Estero", service: "security camera install", minutesAgo: 52 },
  { first: "Christopher", city: "Pelican Bay", service: "Lutron lighting scene", minutesAgo: 68 },
  { first: "Amanda", city: "Cape Coral", service: "network installation", minutesAgo: 74 },
  { first: "James", city: "North Naples", service: "TV mounting + soundbar", minutesAgo: 91 },
  { first: "Patricia", city: "Sanibel", service: "vacation rental tech stack", minutesAgo: 105 },
  { first: "William", city: "Naples", service: "IT support call-out", minutesAgo: 118 },
  { first: "Elizabeth", city: "Bonita Bay", service: "Frame TV install", minutesAgo: 132 },
  { first: "Thomas", city: "Grey Oaks", service: "smart doorbell + camera", minutesAgo: 145 },
  { first: "Nancy", city: "Lehigh Acres", service: "computer repair", minutesAgo: 160 },
  { first: "Charles", city: "Marco Island", service: "outdoor Sonos install", minutesAgo: 175 },
];

const MAX_TOASTS = 5;
const INITIAL_DELAY_MS = 20_000;
const MIN_GAP_MS = 45_000;
const MAX_GAP_MS = 75_000;
const AUTO_DISMISS_MS = 8_000;
const SESSION_KEY = "sp_toast_shown";
const DISMISSED_KEY = "sp_toast_dismissed";

function formatTimeAgo(mins) {
  if (mins < 60) return `${mins} min ago`;
  const h = Math.floor(mins / 60);
  return h === 1 ? "1 hour ago" : `${h} hours ago`;
}

export default function SocialProofToast() {
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(DISMISSED_KEY)) return;

    let mounted = true;
    let timeoutId;

    const shownCount = () => Number(sessionStorage.getItem(SESSION_KEY) || 0);
    const incrementShown = () => sessionStorage.setItem(SESSION_KEY, String(shownCount() + 1));

    const scheduleNext = (initial = false) => {
      const gap = initial ? INITIAL_DELAY_MS : MIN_GAP_MS + Math.random() * (MAX_GAP_MS - MIN_GAP_MS);
      timeoutId = setTimeout(() => {
        if (!mounted) return;
        if (shownCount() >= MAX_TOASTS) return;
        // Pick a random booking, weighted toward recent ones (top of array)
        const idx = Math.floor(Math.random() * BOOKINGS.length);
        setCurrent({ ...BOOKINGS[idx], id: Date.now() });
        incrementShown();
        // Auto-dismiss and schedule next
        setTimeout(() => mounted && setCurrent(null), AUTO_DISMISS_MS);
        scheduleNext(false);
      }, gap);
    };

    scheduleNext(true);
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, []);

  const dismiss = (e) => {
    if (e) e.stopPropagation();
    setCurrent(null);
    sessionStorage.setItem(DISMISSED_KEY, "1");
  };

  const onToastClick = () => {
    trackEvent("social_proof_toast_click", { city: current?.city, service: current?.service });
    trackBookingAttribution("social_proof_toast", "click", {
      city: current?.city,
      service: current?.service,
    });
  };

  if (!current) return null;

  return (
    <div
      data-testid="social-proof-toast"
      className="fixed bottom-24 left-4 md:left-6 z-40 max-w-xs animate-fade-in-up"
      style={{ animationDuration: "0.4s" }}
    >
      <Link
        to="/contact"
        onClick={onToastClick}
        data-testid="social-proof-cta"
        className="block glass-card border border-blue-500/20 rounded-xl px-4 py-3 pr-8 shadow-2xl hover:border-blue-400/40 transition-colors relative"
        style={{ boxShadow: "0 20px 50px -15px rgba(59, 130, 246, 0.3)" }}
      >
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="social-proof-close"
          className="absolute top-1 right-1 inline-flex items-center justify-center min-w-[32px] min-h-[32px] rounded-full text-slate-500 hover:text-white hover:bg-white/5 transition"
        >
          <X size={14} />
        </button>
        <div className="flex items-start gap-2.5">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center">
            <CheckCircle2 size={16} className="text-green-400" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs text-white font-medium leading-snug">
              {current.first} in {current.city} just booked a {current.service}
            </p>
            <p className="mt-1 text-[11px] text-slate-500 flex items-center gap-1">
              <MapPin size={10} />
              {current.city}, FL · {formatTimeAgo(current.minutesAgo)}
            </p>
          </div>
        </div>
      </Link>
    </div>
  );
}
